"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "./utils";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

// forwardRef: o app roda React 18 — sem ele, o ref de um gatilho aninhado
// (ex.: dica sobre um menu) se perde e a dica abre fora da tela.
const DialogTrigger = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>((props, ref) => <DialogPrimitive.Trigger ref={ref} data-slot="dialog-trigger" {...props} />);
DialogTrigger.displayName = 'DialogTrigger';

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  mainArea = false,
  style,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  /** Cobre só a área principal: a barra lateral (--app-sidebar-w) continua
   *  visível e clicável. Usar com <Dialog modal={false}>. Clicar na barra
   *  não fecha o diálogo — a navegação é que desmonta a página. */
  mainArea?: boolean;
}) {
  const mainLeft = 'var(--app-sidebar-w, 0px)';
  const mainWidth = `(100vw - ${mainLeft})`;
  // Abaixo do topo e da barra de abertos (--app-content-top, AppLayout).
  const mainTop = 'var(--app-content-top, 0px)';
  const mainHeight = `(100vh - ${mainTop})`;
  return (
    <DialogPortal data-slot="dialog-portal">
      {mainArea ? (
        <div
          data-slot="dialog-overlay"
          aria-hidden
          className="fixed inset-y-0 right-0 z-50 bg-black/50 animate-in fade-in-0"
          style={{ left: mainLeft, top: mainTop }}
        />
      ) : (
        <DialogOverlay />
      )}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        style={mainArea
          ? {
            left: `calc(${mainLeft} + ${mainWidth} / 2)`,
            width: `calc(${mainWidth} * 0.94)`,
            top: `calc(${mainTop} + ${mainHeight} / 2)`,
            maxHeight: `calc(${mainHeight} * 0.94)`,
            ...style,
          }
          : style}
        onInteractOutside={(e) => {
          if (mainArea) {
            const target = e.target as HTMLElement | null;
            // Barra lateral, topo e barra de abertos navegam (sem fechar antes);
            // avisos não fecham o card; nem a confirmação do app
            // (ConfirmDialogProvider, que fica "fora" do card para o Radix).
            if (target?.closest('[data-app-sidebar], [data-app-topbar], [data-open-tabs-bar], [data-sonner-toaster], [role="alertdialog"], [data-slot="alert-dialog-overlay"]')) {
              e.preventDefault();
              return;
            }
          }
          onInteractOutside?.(e);
        }}
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          // Nunca maior que a tela: sem isso, um formulário comprido saía por
          // cima e por baixo (em notebook de 720px o botão de confirmar sumia).
          // O card (mainArea) já recebe maxHeight pelo style.
          !mainArea && "max-h-[calc(100dvh-2rem)] overflow-y-auto",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
